import React, { useState } from 'react';
import { useContent } from '../../../hooks/useContent';
import { useToast } from '../../../hooks/useToast';
import type { PageContent } from '../../../types';
import { SpinnerIcon } from '../../icons/SpinnerIcon';
import { XIcon } from '../../icons/XIcon';
import { ConfirmationModal } from '../../ConfirmationModal';

const CORE_PAGES = new Set(['home', 'about', 'help', 'terms', 'contact', 'disclaimer', 'dashboard']);

export const ManagePages: React.FC = () => {
    const { content, addPage, deletePage } = useContent();
    const { showToast } = useToast();

    const [newPageId, setNewPageId] = useState('');
    const [newPageH1, setNewPageH1] = useState('');
    const [pageError, setPageError] = useState('');
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [pageToDelete, setPageToDelete] = useState<string | null>(null);

    const handleAddPage = async (e: React.FormEvent) => {
        e.preventDefault();
        setPageError('');
        if (!newPageId || !newPageH1) {
            setPageError('Slug and H1 Title are required.');
            return;
        }
        const sanitizedId = newPageId.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        if (content[sanitizedId]) {
            setPageError('A page with this slug already exists.');
            return;
        }
        setIsPageLoading(true);
        try {
            const newPageContent: PageContent = {
                h1: newPageH1,
                body: `<p>This is a new page. Edit this content.</p>`,
                seo: {
                    title: newPageH1,
                    description: `Description for ${newPageH1}`,
                    canonical: `https://www.brandgenius.app/${sanitizedId}/`,
                    // FIX: Ensure trailing slash is added to slug for consistency with router
                    slug: `/${sanitizedId}/`,
                    noindex: false,
                    customHead: '',
                },
            };
            await addPage(sanitizedId, newPageContent);
            showToast('Page created successfully!');
            setNewPageId('');
            setNewPageH1('');
        } catch (error) {
            setPageError('Failed to create page.');
        } finally {
            setIsPageLoading(false);
        }
    };
    
    const handleConfirmDeletePage = async () => {
        if (!pageToDelete) return;
        try {
            await deletePage(pageToDelete);
            showToast('Page deleted successfully!');
        } catch (error) {
            showToast('Failed to delete page.');
        } finally {
            setPageToDelete(null);
        }
    };

    return (
        <>
            <div className="space-y-6">
                <form onSubmit={handleAddPage} className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end p-4 bg-white rounded-lg border">
                    <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">New Page H1 Title</label>
                        <input type="text" value={newPageH1} onChange={e => setNewPageH1(e.target.value)} placeholder="My Awesome Page" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">URL Slug (e.g., my-awesome-page)</label>
                        <input type="text" value={newPageId} onChange={e => setNewPageId(e.target.value)} placeholder="my-awesome-page" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <button type="submit" disabled={isPageLoading} className="w-full flex justify-center items-center py-2 px-4 bg-indigo-600 text-white rounded-md disabled:opacity-50">{isPageLoading ? <SpinnerIcon/> : 'Create Page'}</button>
                    {pageError && <p className="sm:col-span-5 text-sm text-red-600">{pageError}</p>}
                </form>
                <div className="space-y-2">
                    <h3 className="text-md font-semibold text-indigo-800">Existing Pages</h3>
                    {Object.entries(content).map(([id, page]) => {
                        const pageContent = page as PageContent;
                        return (
                        <div key={id} className="bg-white p-3 rounded-md border flex justify-between items-center text-sm">
                            <div>
                                <p className="font-medium text-gray-800">{pageContent?.h1 || id}</p>
                                <p className="text-gray-500 font-mono">{pageContent?.seo?.slug || `/${id}/`}</p>
                            </div>
                            <button onClick={() => setPageToDelete(id)} disabled={CORE_PAGES.has(id)} className="ml-4 text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed" aria-label={`Delete page ${pageContent?.h1 || id}`}>
                                <XIcon className="h-5 w-5"/>
                            </button>
                        </div>
                    )})}
                </div>
            </div>
            <ConfirmationModal
                isOpen={!!pageToDelete}
                onClose={() => setPageToDelete(null)}
                onConfirm={handleConfirmDeletePage}
                title="Confirm Page Deletion"
                message={`Are you sure you want to delete the "${pageToDelete}" page? This action cannot be undone.`}
            />
        </>
    );
};